import React, { useEffect, useState } from 'react';
import moment from 'moment';
import noop from 'lodash/noop';
import styled from 'styled-components';

import { Body, Section } from 'src/presentation/components/forms/Layout';
import { Button } from 'src/presentation/components/agent_desktop_components';
import CampaignDetailsSection from './CampaignDetailsSection';
import { CampaignSectionRow } from './RecipientCollectionsLayout';
import { ControlLabel, ControlDescription } from 'src/presentation/components/agent_desktop_components';
import ConfirmCancelModal from 'src/presentation/components/forms/ConfirmCancelModal';
import DateTimeRange from './DateTimeRange';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import FieldToggle from 'src/presentation/components/forms/FieldToggle';
import Footer from 'src/presentation/components/forms/Footer';
import Form, { useForm } from 'src/presentation/components/forms/Form';
import { getCsvHref } from './helper';
import { H1 } from '@ad-scripts/presentation/components/common/headers';
import recipientCollectionsResource from 'src/actions/campaigns/recipientCollectionsResource';
import UploadCsvButton from './UploadCsvButton';
import useDirtyFormConfirmation from 'src/presentation/components/pages/shared/hooks/useDirtyFormConfirmation';
import useOnSaveRecipientCollection from 'src/presentation/components/pages/proactiveCommunications/hooks/useOnSaveRecipientCollection';
import { STATUS_PERSISTING, STATUS_SUCCEEDED, useResourceValues } from 'src/presentation/hooks/useResource';

export default function AddRecipientsForm({ campaign, item, root, 'data-aid': dataAid, startDateTimeDescription }) {
  const [endDateEnabled, setEndDateEnabled] = useState(false);
  const [isComplete, setComplete] = useState<boolean>();

  const { add, update, validate } = recipientCollectionsResource.effects;
  const { resetErrors } = recipientCollectionsResource.actions;
  const { errors, persistingState } = useResourceValues(recipientCollectionsResource);

  const handleSave = useOnSaveRecipientCollection({ campaign, endDateEnabled });

  const currentDate = moment();
  const currentDateStr = currentDate.format('MM/DD/YYYY');
  const currentTimeStr = currentDate.format('HH:mm');
  const defaultEndBeforeDateStr = currentDate.add(1, 'day').format('MM/DD/YYYY');

  const formContext = useForm({
    add,
    errors,
    defaultItem: {
      EndDateToggle: false,
      endBeforeDate: defaultEndBeforeDateStr,
      endBeforeTime: currentTimeStr,
      startAfterDate: currentDateStr,
      startAfterTime: currentTimeStr,
    },
    onSave: handleSave,
    item,
    resetErrors,
    root,
    update,
    validate,
  });

  const { onSave, onClose } = formContext;

  const handleChangeShowEndDate = isEnabled => {
    setEndDateEnabled(isEnabled);
    return isEnabled;
  };

  const { handleCancel, handleStay, isConfirmCancelModalVisible, isModified } = useDirtyFormConfirmation({
    formContext,
    isComplete,
    setComplete,
  });

  useEffect(() => {
    if (persistingState === STATUS_SUCCEEDED) {
      setComplete(true);
    }
  }, [persistingState, setComplete]);

  return (
    <>
      <Form context={formContext}>
        <FormHeader>Add Recipients</FormHeader>
        <FormDescription>Upload the recipients list and set the date and time for your Campaign.</FormDescription>

        <Body data-aid={`${dataAid}editor`}>
          <CampaignDetailsSection campaign={campaign} />

          <Section data-aid={`${dataAid}csv`}>
            <ControlLabel>Recipients</ControlLabel>
            <ControlDescription>
              <span>
                The default timezone used to contact customers is set by your organization. Because the customers you
                contact may be in different timezones, you will need to specify a timezone for each recipient on your
                list. To ensure the file follows proper formatting, download our
                <br />
                <ExternalLink to={getCsvHref(campaign.type)} download label="CSV Template" />.
              </span>
            </ControlDescription>
            <UploadCsvButton attr="csvFile" errors={errors} />
          </Section>
          <Section>
            <ControlLabel>Outreach date and time</ControlLabel>
            <ControlDescription>{startDateTimeDescription}</ControlDescription>
            <CampaignSectionRow>
              <FieldToggle
                attr="EndDateToggle"
                dataAid="recipient-collection-toggle-dropdown"
                doNotExpandContent
                label="Set an end date and time for the Campaign."
                onChange={handleChangeShowEndDate}
              >
                {noop}
              </FieldToggle>
            </CampaignSectionRow>
            <DateTimeRangeContainer>
              <DateTimeRange enableEndDate={endDateEnabled} />
            </DateTimeRangeContainer>
          </Section>
        </Body>

        <Footer>
          <Button data-aid={`${dataAid}editor-cancel`} type={Button.Types.TEXT} onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            data-aid={`${dataAid}editor-save`}
            onClick={onSave}
            disabled={!isModified}
            isLoading={persistingState === STATUS_PERSISTING}
          >
            Send
          </Button>
        </Footer>
      </Form>
      <ConfirmCancelModal
        name="Campaign"
        handleLeave={onClose}
        handleStay={handleStay}
        isModified={isModified && !isComplete}
        isVisible={isConfirmCancelModalVisible}
        customText="If you leave this page, your changes will be discarded."
      />
    </>
  );
}

const DateTimeRangeContainer = styled.div`
  margin-top: 8px;
`;

const FormHeader = styled(H1)`
  margin-bottom: 0;
`;

export const FormDescription = styled.p`
  margin-top: 7px;
  margin-bottom: 26px;
  color: ${p => p.theme.colors.gray700};
`;
